import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { browserHistory } from 'common/utils/browserHistory';
import { fetchServiceCases } from 'actions/Case/Contact/Group';
import CasesTable from 'src/components/Facesheet/Cases/components/CasesTable';
import {
  BaseCard,
  BaseCardBody,
  BaseCardHeader,
  Button,
  Pager,
} from '@unite-us/ui';
import { BarLoader } from '@unite-us/app-components';
import { loadDashboardSortingConfiguration } from 'src/components/Dashboard/utils/userSortingHandler';
import { hasCaseManagerRole } from 'common/utils/FeatureFlags/flags';

import './FacesheetCases.scss';

const FacesheetCases = (props) => {
  const [page, setPage] = React.useState(1);
  const sortObj = loadDashboardSortingConfiguration(browserHistory.getCurrentLocation().pathname);
  const defaultSortBy = 'updated_at';
  const defaultSortDirection = 'desc';
  const [sidx, setSidx] = React.useState(sortObj?.column || defaultSortBy);
  const [sord, setSord] = React.useState(sortObj?.sortDirection || defaultSortDirection);

  const fetch = (currentPage = 1, sortBy = sidx, sortDirection = sord) => {
      const { groupId, contactId } = props;
      props.fetchServiceCases(
        groupId,
        contactId,
        {
          page: currentPage,
          sortBy,
          sortDirection,
        },
      );
    };

  React.useEffect(() => {
    if (!props.isFetching && props.route) {
      fetch();
    }
  }, []);

  const onSortChange = React.useCallback((column, direction) => {
    const currentSidx = sidx;
    const currentSord = sord;
    const newSord = direction || ((currentSidx === column && currentSord === 'asc') ? 'desc' : 'asc');
    setSidx(column);
    setSord(newSord);
    setPage(1);
    fetch(1, column, newSord);
  }, [sidx, sord, fetch]);

  const onNextClick = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetch(nextPage);
  };

  const onPrevClick = () => {
    const prevPage = page - 1;
    setPage(prevPage);
    fetch(prevPage);
  };

  const initCaseCreation = () => {
    const { contactId } = props;
    browserHistory.push(`/contacts/${contactId}/cases/new/add-case-details`);
  };

  const {
    contact,
    contactCases,
    isCC,
    isFetching,
    groupId,
    isCaseManager,
    paging,
    showCondensedView = false,
  } = props;

  if (!contact) {
    return null;
  }

  return (
    <BaseCard className="facesheet-cases">
      <BaseCardHeader title={<span className="text-base font-semibold leading-6 text-brand-blue">Cases</span>}>
        {
          contactCases.length > 0 && !showCondensedView && (
            <div className="facesheet-cases__pagination">
              <Pager
                paging={paging}
                disabled={isFetching}
                onNextClick={onNextClick}
                onPrevClick={onPrevClick}
              />
            </div>
          )
        }
        {!isCC && (
        <Button
          id="create-new-case-btn"
          className="facesheet-cases__upload btn-raised btn-success"
          label="Create New Case"
          onClick={initCaseCreation}
          primary
        />
        )}
      </BaseCardHeader>
      <BaseCardBody>
        {
          isFetching ?
            <BarLoader className="h-60" size="full" /> :
            (
              <CasesTable
                cases={contactCases}
                contact={contact}
                groupId={groupId}
                condensedView={showCondensedView}
                sortBy={sidx}
                sortDirection={sord}
                onSortChange={onSortChange}
                isCaseManager={isCaseManager}
              />
            )
        }
      </BaseCardBody>
    </BaseCard>
  );
};
FacesheetCases.propTypes = {
  contact: PropTypes.object.isRequired,
  contactCases: PropTypes.array.isRequired,
  contactId: PropTypes.string.isRequired,
  fetchServiceCases: PropTypes.func.isRequired,
  groupId: PropTypes.string.isRequired,
  isCC: PropTypes.bool.isRequired,
  isCaseManager: PropTypes.bool.isRequired,
  isFetching: PropTypes.bool.isRequired,
  paging: PropTypes.object.isRequired,
  params: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  showCondensedView: PropTypes.bool,
  route: PropTypes.object,
};

function mapStateToProps(state, ownProps) {
  const showCondensedView = ownProps.isClientsDrawer;
  if (showCondensedView) {
    const contactId = _.wget(ownProps, 'id', '');
    const contact = _.wget(ownProps, 'contact', {});
    const contactCaseObj = _.get(state.serviceCase, contactId);
    const contactCases = _.get(contactCaseObj, 'data') || contactCaseObj || [];
    const isCC = _.wget(state, 'session.isCoordinationGroup', false);
    const paging = _.get(contactCaseObj, 'paging', {});
    const isCaseManager = hasCaseManagerRole(state);

    return {
      contact: contact || {},
      contactCases,
      contactId,
      groupId: _.wget(state, 'session.groupId', ''),
      isCC,
      isCaseManager,
      isFetching: _.get(state, 'serviceCase.isFetching', false),
      paging,
      showCondensedView,
    };
  }
  const contacts = _.get(state, 'contacts.contacts', []);
  const contactId = _.get(ownProps, 'params.id');
  const contactCaseObj = _.get(state.serviceCase, ownProps.params.id);
  const contactCases = _.get(contactCaseObj, 'data', []);
  const isCC = _.wget(state, 'session.isCoordinationGroup', false);
  const paging = _.get(contactCaseObj, 'paging', {});
  const isCaseManager = hasCaseManagerRole(state);

  return {
    contact: _.find(contacts, { id: contactId }) || {},
    contactCases,
    contactId,
    groupId: _.wget(state, 'session.groupId', ''),
    isCC,
    isCaseManager,
    isFetching: _.get(state, 'serviceCase.isFetching', false),
    paging,
  };
}

export default connect(mapStateToProps, {
  fetchServiceCases,
})(FacesheetCases);
